<script setup lang="ts">
import type { Entry } from "contentful";
import type { Selection } from "~/types/CuratedCollection";

const { params } = useRoute();
const { locale, t } = useI18n();
const localePath = useLocalePath();
const { progressBarSteps } = useProgressBar();

// Page meta
definePageMeta({
  middleware: "cached-page",
});

// Hotel data
const { hotels } = useHotels();
const currentHotel = computed(() =>
  hotels.value.find((hotel) => {
    const [continent, city, hotelSlug] = hotel.slug.split("/");
    const {
      continent: continentParam,
      city: cityParam,
      hotelSlug: hotelSlugParam,
    } = params;

    return (
      continent === continentParam &&
      city === cityParam &&
      hotelSlug === hotelSlugParam
    );
  }),
);

if (!currentHotel.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Hotel not found",
  });
}

const propertyCode = computed(() => currentHotel.value?.propertyCode);
if (!propertyCode.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Property code not found",
  });
}

const { data: hotelMarketingData } = await useFetch<
  ContentfulPageResponseWithoutHero<ContentulHotelContent>
>(`/api/v2/properties/hotels/${propertyCode.value}/marketing-page`, {
  key: `hotel-info-${propertyCode.value}-${locale.value}`,
  query: {
    locale,
  },
});

if (!hotelMarketingData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Hotel marketing data not found",
  });
}

const { data: hotelMasterDataResponse } =
  await useGoodBitsFetch<HotelInfoExpand>(
    `/masterdata/v3/properties/${propertyCode.value}`,
    {
      key: `hotel-masterdata-${propertyCode.value}-${locale.value}`,
      query: {
        contentLanguage: CONTENTFUL_LOCALE[locale.value],
        expand: [
          "bar",
          "roofTopBar",
          "hotel",
          "location",
          "coworking",
          "pools",
          "genericFacilities",
        ],
      },
    },
  );

const hotelMasterData = computed(() => hotelMasterDataResponse.value?.hotel);
const hotelLocationData = computed(
  () => hotelMasterDataResponse.value?.location,
);

// Hotel meta
const hasCoordinates = computed(
  () =>
    currentHotel.value?.coordinates?.lat &&
    currentHotel.value?.coordinates?.lon,
);

// Map the Hotel (Production Information) media property (Image's) into a Media Collection model
const mediaCollection = computed(() => {
  const mediaItems = currentHotel.value?.media;
  const sys = { contentType: { sys: { id: "image" } } }; // Property "sys" was not included. Type should always be "image" as the media property only allows images.

  return mediaItems?.map(({ caption, ...media }, i) => ({
    fields: { ...media, ...(i === 0 && { caption }) }, // Exclude "caption" property except for the first item
    sys,
  }));
});

function convertFeatureToRichtext(features: SimpleFeatureCategory[]) {
  return {
    nodeType: "document",
    content: [
      {
        nodeType: "unordered-list",
        content: [
          ...features.map((feature) => ({
            nodeType: "list-item",
            content: [
              {
                nodeType: "paragraph",
                content: [
                  {
                    nodeType: "text",
                    value: feature.title,
                  },
                ],
              },
            ],
          })),
        ],
      },
    ],
  };
}

// Feature collections
const roomFeatures = computed(() => {
  if (!hotelMasterData.value) {
    return {};
  }

  const sys = { contentType: { sys: { id: "contentCard" } } }; // Type should always be "contentCard".
  const selectionGrid = hotelMarketingData.value?.content
    ?.highlightedFeatures?.[0].fields.selectionGrid as Entry[];
  const selection = selectionGrid.reduce((result: Selection[], { fields }) => {
    if (fields.media) {
      result.push({
        fields: {
          title: fields.title as string,
          description: fields.description as CfRichTextNode,
          media: (fields?.media as CfMedia[])?.[0],
        },
        sys,
      });
    }

    return result;
  }, []);

  return {
    id: "room-features",
    title: hotelMasterData.value?.hotelRoom?.title,
    layout: "slider - 3 columns",
    selection,
  };
});
const roomFeaturesCollectionId = computed(
  () => `${propertyCode.value}-room-features-${locale.value}`,
);

const hotelFacilities = computed(() => {
  if (!hotelMasterData.value) {
    return {};
  }

  const sys = { contentType: { sys: { id: "contentCard" } } }; // Type should always be "contentCard".
  const hotelSelections = {
    tab: t("website.pagetype.hotel.facilities.hotel"),
    selection: hotelMasterData.value?.featureCategories[0].subCategories || [],
  };
  const roomSelections = {
    tab: t("website.pagetype.hotel.facilities.rooms"),
    selection:
      hotelMasterData.value?.hotelRoom.featureCategories[0].subCategories || [],
  };

  // Convert a hotel & room featureCategories modol into Curated Collection model
  const selection = [hotelSelections, roomSelections]
    .map(({ tab, selection }) =>
      selection.map(({ title, features }) => ({
        fields: {
          tab: tab,
          title: title,
          ...(features && { description: convertFeatureToRichtext(features) }),
        },
        sys,
      })),
    )
    .flat(); // Merge [a: [item], b:[item] ] into [item, item] because we need one list of items

  return {
    id: "hotel-facilities",
    title: hotelMasterData.value?.featureCategories[0].title,
    layout: "slider - 4 columns",
    selection,
  };
});
const hotelFacilitiesCollectionId = computed(
  () => `${propertyCode.value}-hotel-facilities-${locale.value}`,
);

// Map implementation
const showMap = ref(false);

// Travel data
const travelDataStore = useTravelDataStore();
// @ts-expect-error hotelCodes can only be undefined according to signature
travelDataStore.hotelCodes = propertyCode.value;
if (hotelMasterData.value) {
  // @ts-expect-error hotelName can only be undefined according to signature
  travelDataStore.hotelName = hotelMasterData.value.title;
  // @ts-expect-error currentStep can only be undefined according to signature
  travelDataStore.currentStep = null;
}
travelDataStore.checkTraveldata();

const currentSearchComponentProps = ref({
  start: travelDataStore.arrivalDate,
  end: travelDataStore.departureDate,
  guests: travelDataStore.guestsPerRoom,
  rooms: travelDataStore.numberOfRooms,
  hasShadow: true,
  selectedLocation: propertyCode.value || null,
  headline: hotelMasterData.value?.title,
  showLocationSelect: false,
  needsUpdate: true,
  editing: true,
  editable: true,
  class: "within-hotel-search-bar",
  submitUrl: localePath("/book"),
});

const amenityFeatures = computed(() =>
  [
    ...(Array.isArray(
      hotelMarketingData.value?.content.highlightedFeatures?.[0]?.fields
        ?.selectionGrid,
    )
      ? hotelMarketingData.value?.content.highlightedFeatures?.[0].fields.selectionGrid.map(
          // @ts-expect-error entry is typed as unknown by Contentful
          ({ fields }) => ({
            "@type": "LocationFeatureSpecification",
            title: fields?.title,
            value: true,
          }),
        )
      : []),
    ...(Object.keys(hotelMasterDataResponse.value || {}).includes("bar")
      ? [
          {
            "@type": "LocationFeatureSpecification",
            name: "bar",
            value: true,
          },
        ]
      : []),
    ...(Object.keys(hotelMasterDataResponse.value || {}).includes("roofTopBar")
      ? [
          {
            "@type": "LocationFeatureSpecification",
            name: "rooftop bar",
            value: true,
          },
        ]
      : []),
  ].filter(Boolean),
);

// Contentful page actions
useContentfulPage(hotelMarketingData, {
  setSeoMeta: true,
  pageTypeToTrack: PageTypeTrackingId.webpageHotelProperty,
  ecommerceStage: "detail",
});

// i18n settings
const setI18nParams = useSetI18nParams();
const localizedRouteParams = await useLocalizedRouteParams(
  hotelMarketingData.value.contentfulId,
);
if (localizedRouteParams.value) {
  setI18nParams(localizedRouteParams.value);
}

// Schema.org structured data
useSchemaOrg([
  {
    "@type": "Hotel",
    brand: {
      "@type": "Brand",
      name: "citizenM",
    },
    name: currentHotel.value.name,
    image: currentHotel.value.media?.[0]?.asset[0].src,
    latitude: currentHotel.value.coordinates.lat,
    longitude: currentHotel.value.coordinates.lon,
    address: {
      "@type": "PostalAddress",
      addressLocality: hotelLocationData.value?.municipality,
      postalCode: hotelLocationData.value?.postalCode,
      streetAddress: hotelLocationData.value?.streetAndNumber,
      addressCountry: hotelLocationData.value?.city.country.title,
    },
    checkinTime: hotelMasterData.value?.checkInOut.checkinTime,
    checkoutTime: hotelMasterData.value?.checkInOut.checkoutTime,
    starRating: {
      "@type": "Rating",
      ratingValue: "4",
    },
    amenityFeature: amenityFeatures.value,
  },
]);
</script>

<template>
  <div v-if="currentHotel && hotelMasterData">
    <div class="wrap">
      <NavigationBookingProgressBar
        :hotel-codes="propertyCode"
        :steps="progressBarSteps"
        :current-step="0"
        class="mt32 mb32"
        full-width
      />

      <h1 class="h4 page-hotel__title">
        {{ hotelMasterData.title }}
      </h1>

      <section class="page-hotel__location mt16">
        <span v-if="hotelLocationData">
          {{ hotelLocationData.streetAndNumber }},
          {{ hotelLocationData.postalCode }}
          {{ hotelLocationData.municipality }},
          {{ hotelLocationData.city.country.title }}
        </span>

        <ButtonMap
          v-if="hasCoordinates"
          class="page-hotel__map-toggle"
          size="small"
          variant="tertiary"
          @click="showMap = !showMap"
        >
          {{
            showMap
              ? t("website.pagetype.hotel.hidemap.label")
              : t("website.pagetype.hotel.viewmap.label")
          }}
        </ButtonMap>
      </section>

      <DevMessage v-if="!hasCoordinates" class="mt16">
        Unfortunately, location coordinates are not available
      </DevMessage>
      <section v-else-if="showMap" class="mt16">
        <HotelMapCard
          :hotel="{
            lat: currentHotel.coordinates.lat,
            lng: currentHotel.coordinates.lon,
          }"
          :zoom="10"
        />
      </section>
    </div>

    <ContentfulBookingWidget
      class="page-hotel__booking-widget--top"
      :disabledLocation="true"
      :isSticky="true"
      :submit-label="t('website.booking.booking.search_label_show_rates')"
    />

    <div class="wrap">
      <HR class="mt40" />

      <DevMessage v-if="!mediaCollection" class="mt24">
        Unfortunately, there are no hotel media items
      </DevMessage>
      <ContentfulMediaCollection
        v-else
        :columns="4"
        :rows="2"
        :media="mediaCollection"
        class="mt24"
      />

      <div class="page-hotel__sections">
        <section class="page-hotel__section page-hotel__section__info">
          <h2 class="h5 mt0">
            {{ hotelMasterData.subTitle }}
          </h2>
          <ContentfulAtomRichText
            :content="currentHotel.description"
            class="mt16"
          />

          <ul v-if="hotelLocationData" class="page-hotel__section__usps">
            <li
              class="page-hotel__section__usps__item"
              v-for="({ icon, title }, i) in hotelLocationData?.features"
              :key="i"
            >
              <MaterialIcon
                :hover="false"
                :icon="icon?.icon || 'check'"
                size="24"
              />
              {{ title }}
            </li>
          </ul>
        </section>

        <section
          v-if="hotelMarketingData?.content.banner"
          class="page-hotel__section"
        >
          <ContentfulBanner
            v-for="banner in hotelMarketingData.content.banner"
            :key="banner.sys.id"
            v-bind="banner.fields"
          />
        </section>

        <section class="page-hotel__section">
          <ContentfulCuratedCollection
            :id="roomFeaturesCollectionId"
            :title="roomFeatures.title"
            :layout="roomFeatures.layout"
            :selection="roomFeatures.selection"
          />
        </section>

        <section class="page-hotel__section">
          <ContentfulCuratedCollection
            :id="hotelFacilitiesCollectionId"
            :title="hotelFacilities.title"
            :layout="hotelFacilities.layout"
            :selection="hotelFacilities.selection"
          />
        </section>

        <section
          v-if="propertyCode && hotelMasterDataResponse"
          class="page-hotel__section"
        >
          <GoodbitsPropertyFacilitiesList
            :hotel-data="hotelMasterDataResponse"
            :property-code="propertyCode"
          />
        </section>

        <section
          v-if="hotelMasterData.faqs.faqCategories?.length"
          class="page-hotel__section"
        >
          <ContentfulFaqCategory
            v-for="(fields, index) in hotelMasterData.faqs.faqCategories"
            v-bind="fields"
            :key="index"
            theme="hotel"
          />
        </section>
      </div>
    </div>
    <ClientOnly>
      <BookingWidget
        class="page-hotel__booking-widget--bottom"
        v-bind="currentSearchComponentProps"
      />
    </ClientOnly>
  </div>
</template>

<style lang="scss">
.wrap {
  padding-inline: var(--spacing-4, 1rem);
}

.page-hotel {
  &__title {
    font-size: var(--cm-font-size-2xl, 2rem);
    line-height: var(--cm-line-height, 1.5);

    @include md {
      font-size: var(--cm-font-size-4xl, 3rem);
    }
  }

  &__content {
    margin-block: var(--spacing-14, 56px);

    section {
      margin-block: var(--spacing-6, 32px);

      @include lg {
        margin-block: var(--spacing-12, 48px);
      }
    }
  }

  &__hero-image {
    figure {
      margin: var(--spacing-4, 16px) 0;
    }

    img {
      max-width: 100%;
    }
  }

  &__map {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4, 16px);
    margin-block: var(--spacing-4, 16px);
  }

  &__map-trigger {
    align-self: flex-end;
  }

  &__booking-widget {
    &--top {
      display: none;

      @include md {
        display: block;
        padding-inline: var(--spacing-4, 1rem);
        max-width: var(--max-width, 1200px);
        margin: 0 auto;
      }
    }

    &--bottom {
      position: relative;
      z-index: 10;
      display: block;

      @include md {
        display: none;
      }
    }
  }

  // full-width page

  &__location {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2, 8px);

    @include md {
      flex-direction: row;
      gap: var(--spacing-6, 24px);
      align-items: center;
    }
  }

  &__map-toggle {
    align-self: flex-end;
  }

  &__sections {
    margin-block: var(--spacing-14, 56px);
  }

  &__section {
    margin-block: var(--spacing-8, 32px);

    @include lg {
      margin-block: var(--spacing-20, 80px);
    }

    &__info {
      @include lg {
        max-width: 75%;
      }
    }

    &__usps {
      margin-block: var(--spacing-10, 40px) 0;
      padding: 0;
      list-style: none;

      &__item {
        display: flex;
        gap: var(--spacing-2, 8px);
        font-family: var(--font-family-bold);
        margin-block: var(--spacing-4, 16px) 0;

        @include md {
          font-size: var(--font-size-h8, 20px);
        }
      }
    }
  }
}
</style>
